// Native
import { useEffect, useRef, useState } from "react"
import { Button, ButtonGroup, DialogBody, DialogFooter, HStack, Separator, Text } from "@chakra-ui/react"

// Custom
import { Form, UI } from "components"
import { API, toaster } from "config"
import useBankAccounts from "hooks/useBankAccounts"
import useUser from "hooks/useUser"
import useBanks from "hooks/useBank";

export default function Register({ ...props }){

    const user = useUser()
    const bank_accounts = useBankAccounts()
    const banks = useBanks()

    const submitRef = useRef()

    // JONATAN: Adicionado bank_id e bank_code ao estado inicial
    const [ form, setForm ] = useState(
        {
            method: 'pix',
            pix_type: 5,
            type: 1,
            name: user.data?.name,
            bank_id: '',
            bank_code: ''
        })
    const [ errors, setErrors ] = useState()
    const [ loading, setLoading ] = useState(false)

    const handleMethodChange = (method) => {
        setForm({
            method: method,
            pix_type: 5,
            type: 1,
            name: user.data?.name,
            bank_id: '',     // Reset
            bank_code: '',   // Reset
            pix_key: '',
            agency: '',
            account: '',
            operation: ''
        })
        setErrors(null)
    }

    const handleChange = ({ target }) => {
        const { name, value } = target
        switch(name){
            default: setForm({...form, [name]: value}); break;
        }
    }

    const handleSubmit = async () => {
        setLoading(true)
        try{
            // JONATAN: O objeto enviado agora contém ambos os campos de banco
            await API.post('/bank_accounts', form)
            toaster.create({ title: 'Conta bancária registrada', type: 'success' })
            setForm({ method: 'pix', pix_type: 5, type: 1, name: user.data?.name, bank_id: '', bank_code: '' })
            props.onClose()
            bank_accounts.refetch()
        }catch(err){
            setErrors(err?.response?.data?.errors)
            toaster.create({ title: 'Não foi possível registrar a conta.', description: err?.response?.data?.message, type: 'warning' })
        }
        setLoading(false)
    }

    useEffect(() => {
        setForm({ method: 'pix', pix_type: 5, type: 1, name: user.data?.name, bank_id: '', bank_code: '' })
        setErrors(null)
    }, [props.open])

    return(
        <UI.Dialog title="Adicionar conta/chave pix" loading={loading} {...props}>
            <DialogBody>
                <Form.Container submitRef={submitRef} onSubmit={handleSubmit}>
                    <Form.Segment
                        required
                        label="Tipo de conta:"
                        items={[
                            { label: <HStack><UI.Icon name="pix" /> Chave Pix</HStack>, value: 'pix' },
                            { label: <HStack><UI.Icon name="bank" /> Conta Bancária</HStack>, value: 'account' }
                        ]}
                        value={form?.method}
                        onValueChange={e => handleMethodChange(e.value)}
                        disabled={loading}
                    />

                    <Separator />

                    <UI.Show visible={form?.method === 'pix'}>
                        <Form.Segment
                            required
                            label="Tipo de chave:"
                            items={[{ label: 'Telefone', value:  1}, { label: 'Email', value:  2}, { label: 'CPF', value:  3}, { label: 'CNPJ', value:  4}, { label: 'Chave Aleatória', value:  5}]}
                            value={form?.pix_type}
                            onValueChange={e => setForm({...form, pix_type: e.value })}
                            disabled={loading}
                        />

                        <Form.BankSelect
                            key="bank-pix"
                            required
                            label="Banco:"
                            items={banks.data || []}
                            value={form?.bank_id}
                            // JONATAN: Captura ID e Code simultaneamente
                            onValueChange={(id, code) => setForm({ ...form, bank_id: id, bank_code: code })}
                            disabled={loading || banks.loading}
                            error={errors?.bank_id}
                        />

                        <Form.Text
                            required
                            label="Chave Pix:"
                            name="pix_key"
                            value={form?.pix_key || ''}
                            error={errors?.pix_key}
                            onChange={handleChange}
                            size="lg"
                            fontWeight="bold"
                            helper="Insira sua chave pix.: Telefone, E-mail, CPF, CNPJ ou Chave Aleatória"
                            disabled={loading}
                        />
                    </UI.Show>

                    <UI.Show visible={form?.method === 'account'}>
                        <Form.BankSelect
                            key="bank-account"
                            required
                            label="Banco:"
                            items={banks.data || []}
                            value={form?.bank_id}
                            // JONATAN: Captura ID e Code simultaneamente
                            onValueChange={(id, code) => setForm({ ...form, bank_id: id, bank_code: code })}
                            disabled={loading || banks.loading}
                            error={errors?.bank_id}
                        />

                        <Form.Text
                            required
                            type="number"
                            label="Agência:"
                            name="agency"
                            value={form?.agency || ''}
                            error={errors?.agency}
                            disabled={loading}
                            onChange={handleChange}
                            helper="O número da sua agência bancária com o dígito."
                            size="lg"
                        />

                        <Form.Text
                            required
                            type="number"
                            label="Conta corrente:"
                            name="account"
                            value={form?.account || ''}
                            error={errors?.account}
                            disabled={loading}
                            onChange={handleChange}
                            helper="O número da sua conta corrente com dígito."
                            size="lg"
                        />

                        <Form.Text
                            type="number"
                            label="Operação:"
                            name="operation"
                            value={form?.operation || ''}
                            error={errors?.operation}
                            disabled={loading}
                            onChange={handleChange}
                            helper="O número da operação (se houver)."
                        />
                    </UI.Show>

                    <Form.Text
                        required
                        readOnly
                        label="Titular da conta:"
                        value={user.data?.name}
                        size="sm"
                        disabled={loading}
                        helper="O nome do titular deve ser o mesmo do cadastro."
                    />

                    <UI.Alert size="sm">
                        Você pode ter no máximo <b>2 contas bancárias cadastradas</b>.
                    </UI.Alert>
                </Form.Container>
            </DialogBody>

            <DialogFooter>
                <ButtonGroup size="xs">
                    <Button colorPalette="orange" onClick={() => submitRef.current.click()} loading={loading}>
                        <UI.Icon name="bank" /> Registrar {form.method === 'pix' ? 'chave pix' : 'conta bancária'}
                    </Button>
                    <Button variant="plain" onClick={props.onClose} disabled={loading}><UI.Icon name="close" /> Cancelar</Button>
                </ButtonGroup>
            </DialogFooter>
        </UI.Dialog>
    )
}